import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Combobox,
  Dialog,
  Illustration,
  toast,
  type IllustrationName,
} from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { useWalletActionMutation } from 'hooks/mutations/useWalletActionMutation'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import type { BaseErrorResponse } from 'types/beneficiaries'
import {
  WalletLifecycleActionReasons,
  WalletLifecycleActionTypes,
  type WalletLifecycleAction,
  type WalletLifecycleActionReason,
} from 'types/wallets'

interface WalletActionsDialogProps {
  walletId: string
  walletName: string
  action: WalletLifecycleAction
  onClose: () => void
  onSuccess?: () => void
  invalidateQueryKeys?: string[][]
}

interface WalletActionDialogConfig {
  titleKey: string
  descriptionKey: string
  confirmLabelKey: string
  toastKey: string
  sentryLabel: string
  illustration: IllustrationName
  isDestructive: boolean
}

const walletActionDialogConfig: Record<WalletLifecycleAction, WalletActionDialogConfig> = {
  [WalletLifecycleActionTypes.BLOCK]: {
    titleKey: 'wallets.actions.blockDialogTitle',
    descriptionKey: 'wallets.actions.blockDialogDescription',
    confirmLabelKey: 'wallets.actions.block',
    toastKey: 'wallets.actions.blockSuccess',
    sentryLabel: 'blockWallet',
    illustration: 'Prohibit',
    isDestructive: false,
  },
  [WalletLifecycleActionTypes.UNBLOCK]: {
    titleKey: 'wallets.actions.unblockDialogTitle',
    descriptionKey: 'wallets.actions.unblockDialogDescription',
    confirmLabelKey: 'wallets.actions.unblock',
    toastKey: 'wallets.actions.unblockSuccess',
    sentryLabel: 'unblockWallet',
    illustration: 'SealCheck',
    isDestructive: false,
  },
  [WalletLifecycleActionTypes.TERMINATE]: {
    titleKey: 'wallets.actions.terminateDialogTitle',
    descriptionKey: 'wallets.actions.terminateDialogDescription',
    confirmLabelKey: 'wallets.actions.terminate',
    toastKey: 'wallets.actions.terminateSuccess',
    sentryLabel: 'terminateWallet',
    illustration: 'Warning',
    isDestructive: true,
  },
}

export const WalletActionsDialog = ({
  walletId,
  walletName,
  action,
  onClose,
  onSuccess,
  invalidateQueryKeys = [
    ['walletsV2'],
    ['wallets'],
    [reactQueryName.BULK_CAPABILITIES],
    ['totalBalance'],
    [reactQueryName.CUSTOMER_WALLETS],
  ],
}: WalletActionsDialogProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutate: submitWalletAction, isPending } = useWalletActionMutation()
  const [reason, setReason] = useState<WalletLifecycleActionReason | undefined>()
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const actionConfig = walletActionDialogConfig[action]
  const isReasonEmpty = !reason
  const showReasonError = hasAttemptedSubmit && isReasonEmpty

  const reasonOptions = Object.values(WalletLifecycleActionReasons).map(value => ({
    value,
    leftText: value.replaceAll('_', ' '),
  }))

  const handleConfirm = () => {
    setHasAttemptedSubmit(true)
    if (isReasonEmpty) return

    submitWalletAction(
      { walletId, action, comment: reason },
      {
        onSuccess: () => {
          toast({
            header: t(actionConfig.toastKey, { walletName }),
            variant: 'success',
          })
          invalidateQueryKeys.forEach(key => {
            queryClient.invalidateQueries({ queryKey: key })
          })
          onSuccess?.()
          onClose()
        },
        onError: (error: BaseErrorResponse) => {
          logSentryErrorAndGetEventId(actionConfig.sentryLabel, error)
          const errorMessage: string = error?.message || t('wallets.actions.actionError')
          toast({
            header: errorMessage,
            variant: 'error',
          })
        },
      }
    )
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(actionConfig.titleKey, { walletName })}
      Illustration={<Illustration name={actionConfig.illustration} />}
      ActionButtons={[
        <Button
          key="confirm"
          onClick={handleConfirm}
          loading={isPending}
          {...(actionConfig.isDestructive && { variant: 'destructive' })}
        >
          {t(actionConfig.confirmLabelKey)}
        </Button>,
        <Button key="cancel" onClick={onClose} variant="secondary">
          {t('cancel')}
        </Button>,
      ]}
    >
      <Box flexCol gapY={16}>
        <p className="text-foreground-prominent text-body-regular text-center">
          {t(actionConfig.descriptionKey)}
        </p>
        <Combobox
          id="wallet-action-reason-combobox"
          testId="wallet-action-reason-combobox"
          label={t('wallets.actions.reasonLabel')}
          selectPlaceholder={t('wallets.actions.reasonPlaceholder')}
          items={reasonOptions}
          defaultValue={reason ?? ''}
          onChange={value => setReason(value as WalletLifecycleActionReason | undefined)}
          errorText={
            showReasonError
              ? t('fieldRequired', { field: t('wallets.actions.reasonLabel') })
              : undefined
          }
        />
      </Box>
    </Dialog>
  )
}
